import { useEffect } from 'react';
import { toast } from '@/lib/utils/toast';
import { useLocation, useNavigate } from 'react-router-dom';

/**
 * Hook options for customizing the checkout status behavior
 */
export interface StripeCheckoutStatusOptions {
  /**
   * Whether to clean the URL by removing status parameters
   * @default true
   */
  shouldCleanUrl?: boolean;
  
  /**
   * Custom success message to display when payment is successful
   * @default "Your booking is confirmed! Please check your email for details."
   */
  customSuccessMessage?: string;
  
  /**
   * Custom cancel message to display when payment is canceled
   * @default "Your booking was canceled. No payment was processed."
   */
  customCancelMessage?: string;

  /**
   * Callback function called when payment is successful
   */
  onSuccess?: (sessionId?: string) => void;

  /**
   * Callback function called when payment is canceled
   */
  onCancel?: () => void;
}

/**
 * Hook for handling Stripe checkout status parameters.
 * 
 * This hook will check for URL parameters after a Stripe checkout
 * and display appropriate toast notifications based on the status.
 * 
 * @example
 * // Basic usage with default messages
 * useStripeCheckoutStatus();
 * 
 * @example
 * // Custom messages
 * useStripeCheckoutStatus({
 *   customSuccessMessage: "Thanks for booking the Art House experience!",
 *   customCancelMessage: "Your Art House booking was canceled."
 * });
 */
export function useStripeCheckoutStatus(options?: StripeCheckoutStatusOptions) {
  const location = useLocation();
  const navigate = useNavigate();
  
  // Set default options
  const {
    shouldCleanUrl = true,
    customSuccessMessage = "Your booking is confirmed! Please check your email for details.",
    customCancelMessage = "Your booking was canceled. No payment was processed.",
    onSuccess,
    onCancel
  } = options || {};
  
  useEffect(() => {
    // Parse the URL parameters
    const params = new URLSearchParams(location.search);
    const status = params.get('status');
    const sessionId = params.get('session_id');
    
    // Handle success status
    if (status === 'success') {
      toast.success(customSuccessMessage);
      
      console.log('✅ Payment successful', { sessionId });
      
      // Call success callback if provided
      onSuccess?.(sessionId || undefined);
    } 
    // Handle canceled status
    else if (status === 'canceled') {
      toast.error(customCancelMessage);
      
      console.log('❌ Payment canceled');
      
      // Call cancel callback if provided
      onCancel?.();
    }
    
    // Clean up the URL by removing the status parameters if needed
    if (shouldCleanUrl && (status === 'success' || status === 'canceled')) {
      // Remove the status and session_id parameters, but keep any other parameters
      params.delete('status');
      params.delete('session_id');
      
      const newSearch = params.toString();
      const newPath = location.pathname + (newSearch ? `?${newSearch}` : '');
      
      // Replace the current URL without triggering a navigation
      navigate(newPath, { replace: true });
    }
  }, [location.search, navigate, shouldCleanUrl, customSuccessMessage, customCancelMessage]);
  
  // This hook doesn't return anything as it only performs side effects
  return null;
}

export default useStripeCheckoutStatus;
